import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  userService,
  type UpdatePreferencesData,
} from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';

type ThemeMode = 'light' | 'dark' | 'system';

const GETTING_STARTED_STORAGE_KEY = 'crmd_showGettingStarted';

export const UserSettings = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const initializedRef = useRef(false);
  const successTimeoutRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);

  // Local state
  const [theme, setTheme] = useState<ThemeMode>('system');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [browserNotifications, setBrowserNotifications] = useState(false);
  const [riskAlerts, setRiskAlerts] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Getting Started visibility (localStorage)
  const [showGettingStarted, setShowGettingStarted] = useState(() => {
    const stored = localStorage.getItem(GETTING_STARTED_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  const handleShowGettingStartedChange = (event: {
    target: { checked: boolean };
  }) => {
    const checked = event.target.checked;
    setShowGettingStarted(checked);
    localStorage.setItem(GETTING_STARTED_STORAGE_KEY, String(checked));
  };

  // Fetch user profile for preferences
  const {
    data: profile,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['userProfile'],
    queryFn: userService.getMyProfile,
  });

  // Initialize state from profile (only once when data first loads)
  useEffect(() => {
    if (profile?.preferences && !initializedRef.current) {
      initializedRef.current = true;
      const prefs = profile.preferences;
      // Use a microtask to avoid the React warning about setState in effect
      globalThis.queueMicrotask(() => {
        setTheme(prefs.theme || 'system');
        setEmailNotifications(prefs.notifications?.email ?? true);
        setBrowserNotifications(prefs.notifications?.browser ?? false);
        setRiskAlerts(prefs.notifications?.riskAlerts ?? true);
        setWeeklyDigest(prefs.notifications?.weeklyDigest ?? false);
      });
    }
  }, [profile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        globalThis.clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdatePreferencesData) =>
      userService.updateMyPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      refreshUser();
      setHasChanges(false);
      setSaveSuccess(true);
      setSaveError(null);
      successTimeoutRef.current = globalThis.setTimeout(
        () => setSaveSuccess(false),
        3000
      );
    },
    onError: (err: Error) => {
      setSaveError(err.message);
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      theme,
      notifications: {
        email: emailNotifications,
        browser: browserNotifications,
        riskAlerts,
        weeklyDigest,
      },
    });
  };

  const handleThemeChange = (_event: unknown, newTheme: ThemeMode | null) => {
    if (newTheme) {
      setTheme(newTheme);
      setHasChanges(true);
    }
  };

  const handleNotificationChange =
    (setter: React.Dispatch<React.SetStateAction<boolean>>) =>
    (event: { target: { checked: boolean } }) => {
      setter(event.target.checked);
      setHasChanges(true);
    };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    // Language is stored in localStorage by i18next
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{t('settings.loadError')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('settings.title')}
      </Typography>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('settings.saveSuccess')}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      {/* Display Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('settings.display')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.displayDescription')}
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={showGettingStarted}
                onChange={handleShowGettingStartedChange}
              />
            }
            label={t('settings.showGettingStarted')}
          />
        </FormGroup>
      </Paper>

      {/* Language Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('settings.language')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.languageDescription')}
        </Typography>

        <ToggleButtonGroup
          value={i18n.language}
          exclusive
          onChange={(_, lang) => lang && handleLanguageChange(lang)}
          aria-label="language"
        >
          <ToggleButton value="nb" aria-label="Norwegian">
            🇳🇴 Norsk
          </ToggleButton>
          <ToggleButton value="en" aria-label="English">
            🇬🇧 English
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Theme Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('settings.theme')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.themeDescription')}
        </Typography>

        <ToggleButtonGroup
          value={theme}
          exclusive
          onChange={handleThemeChange}
          aria-label="theme"
        >
          <ToggleButton value="light" aria-label="light mode">
            <LightModeIcon sx={{ mr: 1 }} />
            {t('settings.themeLight')}
          </ToggleButton>
          <ToggleButton value="dark" aria-label="dark mode">
            <DarkModeIcon sx={{ mr: 1 }} />
            {t('settings.themeDark')}
          </ToggleButton>
          <ToggleButton value="system" aria-label="system mode">
            <SystemIcon sx={{ mr: 1 }} />
            {t('settings.themeSystem')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Notification Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('settings.notifications')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.notificationsDescription')}
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={emailNotifications}
                onChange={handleNotificationChange(setEmailNotifications)}
              />
            }
            label={t('settings.emailNotifications')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={browserNotifications}
                onChange={handleNotificationChange(setBrowserNotifications)}
              />
            }
            label={t('settings.browserNotifications')}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            {t('settings.alertTypes')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={riskAlerts}
                onChange={handleNotificationChange(setRiskAlerts)}
              />
            }
            label={t('settings.riskAlerts')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={weeklyDigest}
                onChange={handleNotificationChange(setWeeklyDigest)}
              />
            }
            label={t('settings.weeklyDigest')}
          />
        </FormGroup>
      </Paper>

      {/* Save Button */}
      {hasChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={updateMutation.isPending}
            size="large"
          >
            {updateMutation.isPending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('common.saveChanges')
            )}
          </Button>
        </Box>
      )}
    </Container>
  );
};
